import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
} from "react-native";
import AppTheme from "../../AppTheme";
import ManaTextInput from "../../common/ManaTextInput";
import appStyles from "../../../appStyles";
import ManaButton from "../../common/ManaButton";
import fontStyles from "../../../fonts";
import ChipsList from "./ChipsList";
import { hasItems, isAndroid, isWeb } from "../../../../helpers/utils";
import { uniqueId } from "lodash";

export default function Question({
  controller,
  question = {},
  onContinue = () => {},
}) {
  const [hasValidAnswer, setHasValidAnswer] = useState(question.answer);
  const [answersList, setAnswersList] = useState(
    question.answer ? JSON.parse(question.answer) : [""],
  );

  useEffect(() => {
    setAnswersList(question.answer ? JSON.parse(question.answer) : [""]);
  }, [question]);

  useEffect(() => {
    _validateMultiAnswer();
  }, [answersList]);

  const addNewAnswer = () => {
    setAnswersList([...answersList, ""]);
  };

  const updateAnswer = (value, index) => {
    answersList[index] = value;
    _validateMultiAnswer();
  };

  // Multi selection elements
  const updateSelectedElements = (elementIds) => {
    setAnswersList(elementIds);
  };

  const _validateMultiAnswer = () => {
    setHasValidAnswer(!!answersList.filter(Boolean).join(","));
  };

  // ------------------------------------- SAVE -------------------------------------
  const saveAnswer = () => {
    const answer = answersList;

    const action = {
      action: "save",
      target: "question",
      survey: "customize_elements",
      question_id: question.id,
      answer,
    };

    controller.apiService.cardAction(action).then(onContinue);
  };

  const onSkip = () => {
    onContinue();
  };

  return (
    <KeyboardAvoidingView style={appStyles.kbView} behavior="height">
      <ScrollView
        keyboardShouldPersistTaps="always"
        contentContainerStyle={[appStyles.scrollViewContent]}
        style={[
          appStyles.scrollView,
          {
            paddingHorizontal: 0,
          },
        ]}
      >
        <View
          style={{
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 80,
            paddingHorizontal: 10,
          }}
        >
          <Text style={styles.text}>{question.text}</Text>
          <Text style={styles.description}>{question.description}</Text>

          {hasItems(question.elements) ? (
            <ChipsList
              controller={controller}
              elements={question.elements}
              parentId={question.parent_id}
              selection={question.answer}
              updateSelectedElements={updateSelectedElements}
            />
          ) : (
            answersList.map((answer, index) => (
              <ManaTextInput
                style={[
                  styles.input,
                  answer?.length > 12
                    ? styles.inputTextSmall
                    : styles.inputText,
                ]}
                autoFocus={!isWeb && index == answersList.length - 1}
                blurOnSubmit={false}
                key={uniqueId()}
                debounceDelay={400}
                disableBorder
                placeholderTextColor="#9C9EB9"
                placeholder={""}
                onTextChanged={(text) => updateAnswer(text, index)}
                value={answer || ""}
              />
            ))
          )}

          {question.multiple_answers ? (
            <Text
              style={[fontStyles.linkText, { marginTop: 38 }]}
              onPress={addNewAnswer}
            >
              Add {question.model_type}
            </Text>
          ) : null}
        </View>
      </ScrollView>

      {/* Buttons */}
      {isWeb || hasItems(question.elements) ? (
        <View
          style={[
            appStyles.footerButtons,
            { paddingHorizontal: 16, bottom: isWeb ? 16 : 0 },
          ]}
        >
          <ManaButton
            text={"Skip"}
            style={[
              appStyles.bottomButtonTwin,
              appStyles.buttonShadow,
              { marginRight: 8 },
            ]}
            styleText={appStyles.bottomButtonTwinText}
            onPress={onContinue}
          />

          <ManaButton
            isMainButton
            text={"Continue"}
            style={[appStyles.bottomButtonTwin, appStyles.buttonShadow]}
            styleText={appStyles.bottomButtonTwinText}
            onPress={saveAnswer}
            disabled={!hasValidAnswer}
          />
        </View>
      ) : (
        <View
          style={[
            appStyles.footerButtons,
            {
              justifyContent: "space-between",
              paddingHorizontal: 0,
              paddingTop: 0,
              paddingBottom: 0,
              bottom: isAndroid ? 20 : 0,
            },
          ]}
        >
          <Text onPress={onSkip} style={styles.buttonText}>
            Skip
          </Text>

          <Text
            onPress={hasValidAnswer ? saveAnswer : null}
            style={[
              styles.buttonText,
              !hasValidAnswer
                ? { color: "#8B92A1" }
                : {
                    color: AppTheme.DEFAULT_COLOR,
                  },
            ]}
          >
            Continue
          </Text>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    alignItems: "center",
  },
  text: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 20,
    lineHeight: 30,
    letterSpacing: 1,
    color: AppTheme.almostBlack,
    textAlign: "center",
  },
  description: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: AppTheme.almostBlack,
  },
  input: {
    width: 257,
    maxHeight: 50,
    marginTop: 25,
    marginBottom: 12,
    shadowOpacity: 0,
    textAlign: "center",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(45, 49, 66, 0.3)",
    borderRadius: 0,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    color: AppTheme.almostBlack,
    elevation: 0,
  },
  inputText: {
    fontSize: 20,
    lineHeight: 30,
    letterSpacing: 1,
  },
  inputTextSmall: {
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
});
